import styled from '@emotion/styled'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { default as Label } from '../../components/form/Label'
import RichTextEditor from '../../components/global/form/RichTextEditor'
import { CourtType, GameType, Intensity } from '../../types/exercise.type'
import { Skill } from '../../types/skill.type'
import { StyledButton as AdminButton } from '../admin/Login'
import { AdminForm } from '../admin/users/AdminUsersForm'

// Type pour les données du formulaire
type ExerciseFormData = {
    title: string
    description: string
    peopleCount: number
    intensity: Intensity
    gameType: GameType
    courtType: CourtType
    skillIds: number[]
}

interface ExerciseFormProps {
    initial?: ExerciseFormData
    skills: Skill[]
    loading?: boolean
    onSubmit: (data: ExerciseFormData) => void
    onCancel: () => void
}

export default function ExerciseForm({ initial, skills, loading, onSubmit, onCancel }: ExerciseFormProps) {
    const skillGroups = useMemo(() => {
        const groupsMap = new Map<string, Skill[]>()

        // Grouper les compétences par catégorie parente
        skills.forEach((skill) => {
            const groupName = skill.parentSkill?.name ?? 'Autres compétences'
            if (!groupsMap.has(groupName)) {
                groupsMap.set(groupName, [])
            }
            groupsMap.get(groupName)?.push(skill)
        })

        // Convertir en tableau
        const groupsArray = Array.from(groupsMap.entries()).map(([group, skills]) => ({
            group,
            skills: [...skills].sort((a, b) => a.name.localeCompare(b.name)),
        }))

        // Trier les groupes en plaçant 'Autres compétences' à la fin
        return groupsArray.sort((a, b) => {
            if (a.group === 'Autres compétences') return 1
            if (b.group === 'Autres compétences') return -1
            return a.group.localeCompare(b.group)
        })
    }, [skills])

    // Options pour l'Autocomplete (aplatir les groupes)
    const allSkills = useMemo<Skill[]>(() => {
        const skillsList = skillGroups.flatMap((group) => group.skills)
        return skillsList
    }, [skillGroups, initial])

    const [form, setForm] = useState<ExerciseFormData>({
        title: initial?.title ?? '',
        description: initial?.description ?? '',
        peopleCount: initial?.peopleCount ?? 1,
        intensity: initial?.intensity ?? Intensity.MEDIUM,
        gameType: initial?.gameType ?? GameType.BOTH,
        courtType: initial?.courtType ?? CourtType.BOTH,
        skillIds: initial?.skillIds ?? [],
    } as ExerciseFormData)

    // Vérifier la cohérence des IDs de compétences
    useEffect(() => {
        if (allSkills.length > 0 && form.skillIds.length > 0) {
            const validSkillIds = form.skillIds.filter((id) => allSkills.some((skill) => skill.id === id))

            if (validSkillIds.length !== form.skillIds.length) {
                console.warn('Certains IDs de compétences ne sont pas valides. Nettoyage en cours...', {
                    idsInvalides: form.skillIds.filter((id) => !allSkills.some((skill) => skill.id === id)),
                    idsValides: validSkillIds,
                })

                // Mettre à jour les IDs pour ne garder que ceux qui sont valides
                setForm((f) => ({
                    ...f,
                    skillIds: validSkillIds,
                }))
            }
        }
    }, [allSkills, form.skillIds])

    const selectAllInGroup = useCallback(
        (group: string) => {
            const groupSkills = skillGroups.find((g) => g.group === group)?.skills || []
            const groupSkillIds = groupSkills.map((skill) => skill.id)
            const currentSkillIds = new Set(form.skillIds)

            // Si toutes les compétences du groupe sont déjà sélectionnées, on les désélectionne toutes
            const allSelected = groupSkillIds.every((id) => currentSkillIds.has(id))

            const newSkillIds = allSelected
                ? form.skillIds.filter((id) => !groupSkillIds.includes(id))
                : [...new Set([...form.skillIds, ...groupSkillIds])]

            setForm((prev) => ({
                ...prev,
                skillIds: newSkillIds,
            }))
        },
        [form.skillIds, skillGroups]
    )

    const [error, setError] = useState<string | null>(null)

    return (
        <AdminForm
            style={{ minWidth: 320, gap: 18 }}
            onSubmit={(e) => {
                e.preventDefault()
                if (!form.skillIds || form.skillIds.length === 0) {
                    setError('Merci de sélectionner au moins une compétence.')
                    return
                }
                setError(null)
                onSubmit(form)
            }}
        >
            <Label>Titre</Label>
            <input
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                className="form-control"
                required
            />
            <Label>Description</Label>
            <RichTextEditor
                value={form.description ?? ''}
                onChange={(v) => setForm((prev) => ({ ...prev, description: v }))}
            />

            <Label>Nombre de personnes</Label>
            <input
                type="number"
                min={1}
                value={form.peopleCount}
                onChange={(e) => setForm((prev) => ({ ...prev, peopleCount: +e.target.value }))}
                className="form-control"
                required
            />
            <Form.Group className="mb-3">
                <Label>Intensité *</Label>
                <Form.Select
                    value={form.intensity}
                    onChange={(e) => {
                        const value = e.target.value as Intensity
                        setForm((prev) => ({ ...prev, intensity: value }))
                    }}
                    required
                >
                    <option value="LOW">Basse</option>
                    <option value="MEDIUM">Moyenne</option>
                    <option value="HIGH">Haute</option>
                </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
                <Label>Type de jeu *</Label>
                <Form.Select
                    value={form.gameType}
                    onChange={(e) => setForm((prev) => ({ ...prev, gameType: e.target.value as GameType }))}
                    required
                >
                    <option value="SINGLES">Simple</option>
                    <option value="DOUBLES">Double</option>
                    <option value="BOTH">Les deux</option>
                </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
                <Label>Taille de terrain *</Label>
                <Form.Select
                    value={form.courtType}
                    onChange={(e) => setForm((prev) => ({ ...prev, courtType: e.target.value as CourtType }))}
                    required
                >
                    <option value="FULL">Terrain complet</option>
                    <option value="HALF">Demi-terrain</option>
                    <option value="BOTH">Les deux</option>
                </Form.Select>
            </Form.Group>

            <Label>Compétences</Label>
            <Autocomplete
                style={{ width: '100%' }}
                multiple
                options={allSkills}
                groupBy={(option) => {
                    const group = skillGroups.find((g) => g.skills.includes(option))
                    return group?.group ?? 'Autres compétences'
                }}
                getOptionLabel={(option) => option.name}
                value={(() => {
                    const selectedSkills = allSkills.filter((skill) => form.skillIds.includes(skill.id))
                    return selectedSkills
                })()}
                onChange={(_, value) => {
                    const newSkillIds = value.map((v) => v.id)
                    setForm((prev) => ({ ...prev, skillIds: newSkillIds }))
                }}
                disableCloseOnSelect
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        placeholder="Sélectionnez des compétences"
                        fullWidth
                    />
                )}
                renderGroup={(params) => (
                    <div key={params.key}>
                        <div
                            style={{
                                padding: '8px 12px',
                                fontWeight: 'bold',
                                backgroundColor: '#f5f5f5',
                                borderBottom: '1px solid #e0e0e0',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <span>{params.group}</span>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    selectAllInGroup(params.group)
                                }}
                                style={{
                                    background: 'transparent',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    fontSize: '1.6rem',
                                    cursor: 'pointer',
                                    color: '#555',
                                }}
                                title="Sélectionner/désélectionner tout le groupe"
                            >
                                Tout{' '}
                                {form.skillIds.some((id) =>
                                    skillGroups.find((g) => g.group === params.group)?.skills.some((s) => s.id === id)
                                )
                                    ? 'désélectionner'
                                    : 'sélectionner'}
                            </button>
                        </div>
                        <div style={{ padding: '4px 0' }}>{params.children}</div>
                    </div>
                )}
                sx={{
                    width: '100%',
                    background: 'white',
                    borderRadius: 1,
                    '& .MuiAutocomplete-groupLabel': {
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        backgroundColor: '#f5f5f5',
                        padding: '4px 12px',
                    },
                    '& .MuiAutocomplete-groupUl': {
                        padding: 0,
                    },
                }}
            />
            {error && <ErrorMsg>{error}</ErrorMsg>}
            <ButtonBar>
                <AdminButton type="submit" variant="primary" disabled={loading}>
                    {loading ? 'Enregistrement...' : 'Valider'}
                </AdminButton>
            </ButtonBar>
        </AdminForm>
    )
}

const ButtonBar = styled.div`
    display: flex;
    gap: 18px;
    margin-top: 10px;
`

const ErrorMsg = styled.div`
    color: #d32f2f;
    margin-top: 8px;
`
